import { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { Skeleton } from '@/components/ui/skeleton';
import type { Tool } from '@/features/traceability/type/Tool';
import { aggregateToolMetricsByReference } from '../../utils/aggregateToolMetricsByReference';
import { MeanMetricChart } from './MeanMetricChart';

type ReferencePerformanceChartsProps = {
  tools: Tool[];
  selectedReferences: string[];
  isLoading?: boolean;
};

export function ReferencePerformanceCharts({
  tools,
  selectedReferences,
  isLoading = false,
}: ReferencePerformanceChartsProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  const aggregatedData = useMemo(() => {
    if (isLoading || selectedReferences.length === 0) {
      return [];
    }
    return aggregateToolMetricsByReference(tools, selectedReferences).map((item) => ({
      ...item,
      reference: resolve(item.reference).label,
    }));
  }, [tools, selectedReferences, isLoading, resolve]);

  if (isLoading) {
    return (
      <section className="space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[380px] rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (aggregatedData.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="section-title">{t('analytics.comparison.performance.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('analytics.comparison.performance.description')}
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <MeanMetricChart metric="duration" data={aggregatedData} />
        <MeanMetricChart metric="pieces" data={aggregatedData} />
        <MeanMetricChart metric="volume" data={aggregatedData} />
        <MeanMetricChart metric="malround" data={aggregatedData} />
      </div>
    </section>
  );
}
